import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { AcrBulkRescanResponse, AcrResultsResponse } from "../../types/acr";

/**
 * 管理者向け ACR Cloud 結果一覧 API（master_admin 専用）。
 *
 * - `GET /api/admin/projects?q=&limit=&offset=` プロジェクト検索（選択用）
 * - `GET /api/admin/projects/:project_id/acr-results` プロジェクト内 ACR 結果一覧
 * - `POST /api/admin/projects/:project_id/acr-rescan` 照合中(processing)を一括再スキャン
 *
 * 集約 API はショット内メディア / ショットコメント添付 / トークルーム添付の
 * 3 種類をひとまとめに返す。一括再スキャンは Celery 経由で `acr_status='2'`
 * を `'1'` にリセットして File Scanning キューに再投入する（即時に結果が
 * 入れ替わるわけではないため、UI 側ではトースト表示後に手動 / 一定間隔の
 * 再取得が必要）。
 */

export interface AdminProjectSummary {
  project_id: string;
  name: string;
  code_name: string;
  modified: string | null;
}

export interface SearchAdminProjectsResponse {
  status: string;
  data: {
    projects: AdminProjectSummary[];
    count: number;
    limit: number | null;
    offset: number;
    has_next: boolean;
  };
}

export interface SearchAdminProjectsParams {
  q?: string;
  limit?: number;
  offset?: number;
}

const buildSearchProjectsQuery = ({
  q,
  limit,
  offset,
}: SearchAdminProjectsParams): string => {
  const params: Record<string, string> = {};
  if (q !== undefined && q !== "") params.q = q;
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : "";
};

export const adminAcrApi = createApi({
  reducerPath: "adminAcrApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminProjectSearch", "AdminAcrResults"],
  endpoints: (builder) => ({
    searchAdminProjects: builder.query<
      SearchAdminProjectsResponse,
      SearchAdminProjectsParams
    >({
      query: (params) =>
        `/api/admin/projects${buildSearchProjectsQuery(params)}`,
      providesTags: (_result, _error, { q, limit, offset }) => [
        {
          type: "AdminProjectSearch",
          id: `${q ?? ""}:${limit ?? ""}:${offset ?? ""}`,
        },
      ],
    }),

    getAdminAcrResults: builder.query<AcrResultsResponse, string>({
      query: (projectId) => `/api/admin/projects/${projectId}/acr-results`,
      providesTags: (_result, _error, projectId) => [
        { type: "AdminAcrResults", id: projectId },
      ],
    }),

    bulkRescanProcessing: builder.mutation<AcrBulkRescanResponse, string>({
      query: (projectId) => ({
        url: `/api/admin/projects/${projectId}/acr-rescan`,
        method: "POST",
      }),
      // ACR 再スキャンは非同期（Celery）なので、即時に状態が変わるとは
      // 限らない。それでも一覧表示用キャッシュを無効化しておき、ユーザー
      // が refetch / 再訪問したときに最新ステータスを取得できるようにする。
      invalidatesTags: (_result, _error, projectId) => [
        { type: "AdminAcrResults", id: projectId },
      ],
    }),
  }),
});

export const {
  useSearchAdminProjectsQuery,
  useLazySearchAdminProjectsQuery,
  useGetAdminAcrResultsQuery,
  useBulkRescanProcessingMutation,
} = adminAcrApi;
